import {
  EPlanningColumns,
  EPlanningOrderDirection,
  TPlanningFilters,
  TPlanningOrder,
  TPlanningPatientMedEvent,
} from '../planning.type'
import {
  PlanningIdCell,
  PlanningPatientCell,
  PlanningMedEventCell,
  PlanningConvocationCell,
  PlanningMedicalStaffMemberCell,
  PlanningActionsCell,
  PlanningMedicalAlertCell,
  PlanningMedicalTeamCommentCell,
  PlanningDocumentsCell,
} from './planning-cells.component'
import { useTranslation } from 'react-i18next'
import {
  ArrowLineDownIcon,
  ArrowLineTopIcon,
  ArrowTriangleIcon,
  CurecallButton,
  ECurecallButtonAppearance,
  LoadingSpinner,
} from '@honestica/curecall-ui'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNavigate } from 'react-router-dom'

interface PlanningTableProps {
  patientMedEvents?: TPlanningPatientMedEvent[]
  currentTabColumns: EPlanningColumns[]
  currentOrder: TPlanningOrder
  updateOrder: (order: TPlanningOrder) => void
  isLoading?: boolean
  currentRelativeDay: number
  updatePlanningFilters: (filters: Partial<TPlanningFilters>) => void
}
export default function PlanningTable({
  patientMedEvents,
  currentTabColumns,
  currentOrder,
  updateOrder,
  isLoading,
  currentRelativeDay,
  updatePlanningFilters,
}: Readonly<PlanningTableProps>) {
  return (
    // calc(100%-44px-76px-49px) = 100% height of the parent, minus the height of the title (44px), minus the height of the height of tabs (38px), minus the height of the filters (57.2px),  and the pagination (36px)
    <div className="c-h-[calc(100%-44px-38px-57.2px-36px)] c-min-h-0 c-overflow-auto">
      <table className="c-w-full">
        <thead className="c-h-10 c-bg-grey-50 c-sticky c-top-0 c-z-10">
          <PlanningTableHeading
            currentTabColumns={currentTabColumns}
            currentOrder={currentOrder}
            updateOrder={updateOrder}
          />
        </thead>
        <tbody>
          <PlanningTableBody
            isLoading={isLoading}
            patientMedEvents={patientMedEvents}
            currentTabColumns={currentTabColumns}
            currentRelativeDay={currentRelativeDay}
            updatePlanningFilters={updatePlanningFilters}
          />
        </tbody>
      </table>
    </div>
  )
}

const PlanningTableHeading = ({
  currentTabColumns,
  currentOrder,
  updateOrder,
}: {
  currentTabColumns: EPlanningColumns[]
  currentOrder: TPlanningOrder
  updateOrder: (order: TPlanningOrder) => void
}) => {
  const { t } = useTranslation()

  const COLUMN_TO_LABEL = {
    [EPlanningColumns.ID]: { label: t('pages.planning.columns.id'), isSortable: false },
    [EPlanningColumns.PATIENT]: { label: t('pages.planning.columns.patient'), isSortable: true },
    [EPlanningColumns.MED_EVENT]: {
      label: t('pages.planning.columns.med-event'),
      isSortable: true,
    },
    [EPlanningColumns.CONVOCATION]: {
      label: t('pages.planning.columns.convocation'),
      isSortable: true,
    },
    [EPlanningColumns.MEDICAL_STAFF_MEMBER]: {
      label: t('pages.planning.columns.medical-staff-member'),
      isSortable: true,
    },
    [EPlanningColumns.MEDICAL_TEAM_COMMENT]: {
      label: t('pages.planning.columns.medical-team-comment'),
      isSortable: false,
    },
    [EPlanningColumns.PATIENT_MED_ALERT]: {
      label: t('pages.planning.columns.patient-med-alert'),
      isSortable: false,
    },
    [EPlanningColumns.DOCUMENTS]: { label: t('pages.planning.columns.documents'), isSortable: false },
    [EPlanningColumns.ACTIONS]: { label: t('pages.planning.columns.actions'), isSortable: false },
  }

  return (
    <tr>
      {currentTabColumns.map((column) => (
        <th key={column} className="c-p-2 c-text-grey-700">
          <div className="c-flex c-gap-2 c-items-center c-text-nowrap">
            {COLUMN_TO_LABEL[column].isSortable ? (
              <SortableColumn
                column={column}
                currentOrder={currentOrder}
                updateOrder={updateOrder}
                columnLabel={COLUMN_TO_LABEL[column].label}
              />
            ) : (
              COLUMN_TO_LABEL[column].label
            )}
          </div>
        </th>
      ))}
    </tr>
  )
}

const SortableColumn = ({
  column,
  currentOrder,
  updateOrder,
  columnLabel,
}: {
  column: EPlanningColumns
  currentOrder: TPlanningOrder
  updateOrder: (order: TPlanningOrder) => void
  columnLabel: string
}) => {
  const { t } = useTranslation()

  const ColumnButton = ({ orderDirection }: { orderDirection: EPlanningOrderDirection }) => {
    return (
      <button
        type="button"
        aria-label={
          orderDirection === EPlanningOrderDirection.DESC
            ? t('pages.planning.columns.desc-order')
            : t('pages.planning.columns.asc-order')
        }
        onClick={() => updateOrder({ orderBy: column, orderDirection })}
        className="c-flex c-items-center c-gap-1 c-group hover:c-text-grey-600"
      >
        {columnLabel}
        {orderDirection === EPlanningOrderDirection.ASC ? (
          <ArrowLineTopIcon className="tw-w-4 c-text-primary-700 hover:c-text-primary-500 group-hover:c-text-primary-500" />
        ) : (
          <ArrowLineDownIcon className="tw-w-4 c-text-primary-700 hover:c-text-primary-500 group-hover:c-text-primary-500" />
        )}
      </button>
    )
  }

  if (currentOrder.orderBy === column) {
    return currentOrder.orderDirection === EPlanningOrderDirection.ASC ? (
      <ColumnButton orderDirection={EPlanningOrderDirection.DESC} />
    ) : (
      <ColumnButton orderDirection={EPlanningOrderDirection.ASC} />
    )
  }

  return <ColumnButton orderDirection={EPlanningOrderDirection.DESC} />
}

const PlanningTableBody = ({
  patientMedEvents,
  currentTabColumns,
  isLoading,
  currentRelativeDay,
  updatePlanningFilters,
}: {
  patientMedEvents?: TPlanningPatientMedEvent[]
  currentTabColumns: EPlanningColumns[]
  isLoading?: boolean
  currentRelativeDay: number
  updatePlanningFilters: (filters: Partial<TPlanningFilters>) => void
}) => {
  const { t, i18n } = useTranslation()
  const { project } = useCurrentProject()
  const navigate = useNavigate()

  const COLUMN_TO_CELL = {
    [EPlanningColumns.ID]: PlanningIdCell,
    [EPlanningColumns.PATIENT]: PlanningPatientCell,
    [EPlanningColumns.MED_EVENT]: PlanningMedEventCell,
    [EPlanningColumns.CONVOCATION]: PlanningConvocationCell,
    [EPlanningColumns.MEDICAL_STAFF_MEMBER]: PlanningMedicalStaffMemberCell,
    [EPlanningColumns.MEDICAL_TEAM_COMMENT]: PlanningMedicalTeamCommentCell,
    [EPlanningColumns.PATIENT_MED_ALERT]: PlanningMedicalAlertCell,
    [EPlanningColumns.DOCUMENTS]: PlanningDocumentsCell,
    [EPlanningColumns.ACTIONS]: PlanningActionsCell,
  }

  if (isLoading || !patientMedEvents) {
    return (
      <tr>
        <td colSpan={currentTabColumns.length}>
          <div className="c-mt-40 c-flex c-items-center c-justify-center c-h-full c-min-h-0 c-w-full c-flex-col">
            <LoadingSpinner />
          </div>
        </td>
      </tr>
    )
  }

  if (patientMedEvents?.length === 0) {
    return (
      <tr>
        <td colSpan={currentTabColumns.length}>
          <div className="c-mt-20 c-flex c-flex-col c-items-center c-justify-center c-gap-4">
            <p className="c-font-bold c-text-base c-text-center c-m-0">{t('pages.planning.no-result')}</p>

            <div className="c-flex c-gap-8 c-w-full c-justify-center">
              <CurecallButton
                type="button"
                appearance={ECurecallButtonAppearance.SECONDARY}
                onClick={() => updatePlanningFilters({ relativeDaysFromToday: currentRelativeDay - 1 })}
              >
                <ArrowTriangleIcon position="LEFT" className="c-w-4 c-inline-block" />
                {t('pages.planning.previous-day-button')}
              </CurecallButton>

              <CurecallButton
                type="button"
                appearance={ECurecallButtonAppearance.SECONDARY}
                onClick={() => updatePlanningFilters({ relativeDaysFromToday: currentRelativeDay + 1 })}
              >
                {t('pages.planning.next-day-button')}
                <ArrowTriangleIcon position="RIGHT" className="c-w-4 c-inline-block" />
              </CurecallButton>
            </div>

            <img src="/assets/img/no-patient-med-event.svg" alt="" className="c-w-1/4 c-mt-4" />
          </div>
        </td>
      </tr>
    )
  }

  const onClickOnPatient = ({ patientMedEvent }: { patientMedEvent: TPlanningPatientMedEvent }) => {
    const patientId = patientMedEvent?.Patient.id
    const locale = i18n.language
    patientId &&
      navigate(`/${locale}/${project.id}/medical/patients/${patientId}/${patientMedEvent.PatientFollowup.id}`)
  }

  return patientMedEvents.map((patientMedEvent) => (
    <tr
      key={patientMedEvent.id}
      className="hover:c-bg-grey-50 c-border-b c-border-grey-200 last-of-type:c-border-none [&>td]:c-p-2 c-cursor-pointer c-group"
      onClick={() => onClickOnPatient({ patientMedEvent })}
    >
      {currentTabColumns.map((col) => {
        const Cell = COLUMN_TO_CELL[col]
        return <Cell key={col} patientMedEvent={patientMedEvent} />
      })}
    </tr>
  ))
}
